import React, { useMemo, useState, useCallback, useRef, useEffect } from "react"
import {
  Link,
  Table,
  HStack,
  Text,
  VStack,
  Box,
  Button,
  Input,
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverPositioner,
  Portal,
  Skeleton,
  Center,
  Separator,
} from "@chakra-ui/react"
import { FaSortUp, FaSortDown, FaSort, FaFilter, FaXmark, FaAngleUp, FaAngleDown, FaChevronRight } from "react-icons/fa6"
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { SegmentedControl } from "@hydrologiq/shared/chakra/SegmentedControl"
import { NativeSelectRoot, NativeSelectField } from "@hydrologiq/shared/chakra/NativeSelect"
import ErrorAlert from "./ErrorAlert"
import { PaginationMeta, SortConfig, BaseFilters } from "../types/pagination"

// Helper function to generate page numbers for pagination
function getPageNumbers(totalPages: number, currentPage: number): (number | string)[] {
  const delta = 2 // Number of pages to show around current page
  const pages: (number | string)[] = []

  if (totalPages <= 7) {
    // Show all pages if total is 7 or less
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i)
    }
  } else {
    // Always show first page
    pages.push(1)

    if (currentPage > delta + 2) {
      pages.push("...")
    }

    // Show pages around current page
    const start = Math.max(2, currentPage - delta + 1)
    const end = Math.min(totalPages - 1, currentPage + delta + 1)

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }

    if (currentPage < totalPages - delta - 1) {
      pages.push("...")
    }

    // Always show last page if there are multiple pages
    if (totalPages > 1) {
      pages.push(totalPages)
    }
  }

  return pages
}

// Extend TanStack Table's column meta to include filter variants
declare module "@tanstack/react-table" {
  interface ColumnMeta<TData, TValue> {
    filterVariant?: "text" | "select" | "range"
    filterOptions?: Array<{ label: string; value: string }>
    /** When true, column shrinks to fit its content */
    shrink?: boolean
  }
}

/**
 * Server-side filtering configuration
 */
export interface ServerFiltering<F extends BaseFilters = BaseFilters> {
  filters: F
  sort: SortConfig | null
  onFiltersChange: (filters: F) => void
  onSortChange: (sort: SortConfig | null) => void
  onClearAll: () => void
}

/**
 * Server-side pagination configuration
 */
export interface ServerPagination {
  pagination: PaginationMeta
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

/**
 * Configuration for expandable rows in PaginatedTable.
 * All expansion props are optional — when omitted, the table renders without expansion.
 */
export interface ExpandableRowConfig<T> {
  /**
   * Renders the content shown inside the expanded row (wrapped in a colSpan cell).
   * Receives the row's data item typed as T.
   */
  renderExpandedContent?: (item: T) => React.ReactNode

  /**
   * Renders direct table rows (e.g. sub-modules) directly into the parent table body.
   * Receives the row's data item typed as T and the table's column definitions.
   * Allows sub-rows to share the exact same table grid and column alignment as the parent.
   */
  renderExpandedRows?: (item: T, columns: ColumnDef<T, any>[]) => React.ReactNode

  /**
   * Extracts a unique string identifier from a row's data.
   * Used to track which rows are expanded.
   * Falls back to TanStack Table's row.id if not provided.
   */
  getRowId?: (item: T) => string

  /**
   * Optional predicate to determine if a row is expandable.
   * When provided, only rows where this returns true show the expand chevron.
   * Non-expandable rows show a disabled chevron.
   */
  isExpandable?: (item: T) => boolean

  /**
   * Controlled expansion state — a Set of currently expanded row identifiers.
   * When provided alongside onExpandedChange, PaginatedTable uses external state.
   */
  expandedRowIds?: Set<string>

  /**
   * Callback fired when expansion state changes (controlled mode).
   * Receives the new Set of expanded row identifiers.
   */
  onExpandedChange?: (expandedIds: Set<string>) => void

  /**
   * Custom padding for the expanded cell wrapper. Defaults to undefined (uses py={2} px={4}).
   * Set to 0 for flush edge-to-edge child table embedding.
   */
  cellPadding?: string | number
}

/**
 * Generic PaginatedTable props
 */
export interface PaginatedTableProps<T, F extends BaseFilters = BaseFilters> {
  /** Data to display */
  data: T[]
  /** Column definitions for TanStack Table */
  columns: ColumnDef<T, any>[]
  /** Loading state */
  loading?: boolean
  /** Error message */
  error?: string | null
  /** Retry function for error state */
  onRetry?: () => void
  /** Server pagination configuration */
  serverPagination?: ServerPagination
  /** Server filtering configuration */
  serverFiltering?: ServerFiltering<F>
  /** Header actions (like Create button) */
  headerActions?: React.ReactNode
  /** Empty state configuration */
  emptyState?: {
    heading: string
    text: string
  }
  /** Table variant for styling */
  variant?: string
  /** Entity name for UI labels (e.g., "orders", "assets") */
  entityName?: string
  /** Additional export functionality */
  exportComponent?: React.ReactNode
  /** Optional expandable row configuration */
  expandableRows?: ExpandableRowConfig<T>
  /** Optional function to provide per-row style props */
  getRowStyle?: (item: T) => React.CSSProperties | undefined
  /** When true, hides the top controls bar (restore defaults, count, export, actions) */
  hideControls?: boolean
  /** When true, hides the table column headers (thead) */
  hideHeader?: boolean
  /** When true, hides the bottom pagination controls */
  hidePagination?: boolean
  /** When true, strips outer card border/shadow/margin for embedding inside an expanded row */
  isNested?: boolean
}

// Debounced input component for text filters
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return <Input {...props} value={value} onChange={(e) => setValue(e.target.value)} size="sm" />
}

// Filter component for individual columns
function Filter({ column }: { column: any }) {
  const { filterVariant, filterOptions, filterFormatter } = column.columnDef.meta ?? {}
  const columnFilterValue = column.getFilterValue()

  const sortedUniqueValues = useMemo(() => {
    if (filterVariant === "range") {
      return []
    }

    // Use predefined filter options if available
    if (filterOptions) {
      return filterOptions
    }

    const allValues = Array.from(column.getFacetedUniqueValues().keys())

    const uniqueValues = allValues
      .filter((value) => value !== null && value !== undefined && value !== "")
      .sort()
      .slice(0, 5000)

    // Apply custom formatter if provided, otherwise use raw value as label
    return uniqueValues.map((value) => ({
      label: filterFormatter ? filterFormatter(value) : value,
      value,
    }))
  }, [column.getFacetedUniqueValues(), filterVariant, filterOptions, filterFormatter])

  if (filterVariant === "select") {
    return (
      <NativeSelectRoot active={!!columnFilterValue}>
        <NativeSelectField
          value={columnFilterValue?.toString() ?? ""}
          onChange={(e) => column.setFilterValue(e.target.value || undefined)}
          items={[{ label: "All", value: "" }, ...sortedUniqueValues]}
        />
      </NativeSelectRoot>
    )
  }

  // Text filter (default)
  return (
    <DebouncedInput
      type="text"
      value={(columnFilterValue ?? "") as string}
      onChange={(value) => column.setFilterValue(value)}
      placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
      list={column.id + "list"}
    />
  )
}

// Color constants for consistent theming
const COLORS = {
  ACTIVE: "active", // Purple for active states
  HOVER: "highlight", // Blue for hover/open states
  INACTIVE: "primary_light", // Gray for inactive states
}

// Column header with combined sort and filter capabilities in a single popover
function ColumnHeader({ header, columnFilters }: { header: any; columnFilters: ColumnFiltersState }) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  // Check if this column has an active filter
  const hasActiveFilter = columnFilters.some((filter) => filter.id === header.column.id && filter.value)

  // Get sort state
  const sortState = header.column.getIsSorted()

  // Check if column has any sort or filter capabilities
  const hasCapabilities = header.column.getCanSort() || header.column.getCanFilter()

  return (
    <HStack gap={1.5} align="center" display="inline-flex">
      <Box flex="0 1 auto" minW="min-content">
        <Box
          as="span"
          display="inline-block"
          fontWeight="medium"
          fontSize="0.75rem"
          lineHeight="1.15"
          letterSpacing="0.03em"
          textTransform="uppercase"
          wordBreak="normal"
          overflowWrap="normal"
          css={{ hyphens: "none" }}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
        </Box>
      </Box>

      {/* Visual indicators - clickable triggers for popover */}
      {hasCapabilities && (
        <HStack gap="0.2rem" flexShrink={0} align="center">
          {/* Sort indicator */}
          {header.column.getCanSort() && (
            <Box
              display="inline-flex"
              alignItems="center"
              color={isPopoverOpen && !sortState ? COLORS.HOVER : sortState ? COLORS.ACTIVE : COLORS.INACTIVE}
              cursor="pointer"
              onClick={() => setIsPopoverOpen(!isPopoverOpen)}
              _hover={{ color: sortState ? undefined : COLORS.HOVER }}
            >
              {sortState === "asc" ? (
                <FaSortUp size={11} />
              ) : sortState === "desc" ? (
                <FaSortDown size={11} />
              ) : (
                <FaSort size={11} />
              )}
            </Box>
          )}

          {/* Filter indicator */}
          {header.column.getCanFilter() && (
            <Box
              display="inline-flex"
              alignItems="center"
              color={
                isPopoverOpen && !hasActiveFilter ? COLORS.HOVER : hasActiveFilter ? COLORS.ACTIVE : COLORS.INACTIVE
              }
              cursor="pointer"
              onClick={() => setIsPopoverOpen(!isPopoverOpen)}
              _hover={{ color: hasActiveFilter ? undefined : COLORS.HOVER }}
            >
              <FaFilter size={9.5} />
            </Box>
          )}
        </HStack>
      )}

      {/* Combined sort/filter popover - triggered by icons */}
      {hasCapabilities && (
        <PopoverRoot open={isPopoverOpen} onOpenChange={({ open }) => setIsPopoverOpen(open)}>
          <PopoverTrigger asChild>
            <Box as="span" display="inline-block" w="0px" h="0px" overflow="hidden" />
          </PopoverTrigger>
          <Portal>
            <PopoverPositioner zIndex={1500}>
              <PopoverContent w="250px">
              <PopoverBody>
                <VStack gap={3} align="stretch">
                  <Text fontSize="sm" fontWeight="medium">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Text>

                  {/* Sort options */}
                  {header.column.getCanSort() && (
                    <HStack gap={2} align="stretch" justifyContent="space-between">
                      <Center w="100px" justifyContent="flex-start">
                        <Text fontSize="xs" fontWeight="medium" color="gray.600">
                          Sort by
                        </Text>
                      </Center>
                      <HStack gap={2} justifyContent="space-between" width="100%">
                        <Box>
                          {sortState && (
                            <Button
                              variant="hydrologiq_icon"
                              size="sm"
                              p={1}
                              onClick={() => {
                                header.column.clearSorting()
                                setIsPopoverOpen(false)
                              }}
                            >
                              <FaXmark size={12} />
                            </Button>
                          )}
                        </Box>
                        <SegmentedControl
                          value={sortState === "asc" ? "asc" : sortState === "desc" ? "desc" : ""}
                          onValueChange={(details) => {
                            if (details.value === "asc") {
                              header.column.toggleSorting(false)
                            } else if (details.value === "desc") {
                              header.column.toggleSorting(true)
                            } else {
                              header.column.clearSorting()
                            }
                            setIsPopoverOpen(false)
                          }}
                          size="sm"
                          variant="hydrologiq"
                          items={[
                            {
                              value: "asc",
                              label: "Asc",
                              icon: FaAngleUp,
                            },
                            {
                              value: "desc",
                              label: "Desc",
                              icon: FaAngleDown,
                            },
                          ]}
                        />
                      </HStack>
                    </HStack>
                  )}

                  {/* Separator between sort and filter */}
                  {header.column.getCanSort() && header.column.getCanFilter() && <Separator />}

                  {/* Filter options */}
                  {header.column.getCanFilter() && (
                    <HStack gap={2} align="stretch" justifyContent="space-between">
                      <Center w="100px" justifyContent="flex-start">
                        <Text fontSize="xs" textAlign="left" fontWeight="medium" color="gray.600">
                          Filter by
                        </Text>
                      </Center>
                      <HStack width="100%" justifyContent="space-between">
                        {hasActiveFilter && (
                          <Button
                            variant="hydrologiq_icon"
                            size="sm"
                            p={1}
                            onClick={() => {
                              header.column.setFilterValue(undefined)
                              setIsPopoverOpen(false)
                            }}
                          >
                            <FaXmark size={12} />
                          </Button>
                        )}
                        <Filter column={header.column} />
                      </HStack>
                    </HStack>
                  )}
                </VStack>
              </PopoverBody>
            </PopoverContent>
            </PopoverPositioner>
          </Portal>
        </PopoverRoot>
      )}
    </HStack>
  )
}

/**
 * Internal hook for managing row expansion state.
 * Supports both controlled (external state) and uncontrolled (internal state) modes.
 * Clears expansion state when the page changes.
 */
function useExpansionState<T>(
  config: ExpandableRowConfig<T> | undefined,
  pageIndex: number
): {
  expandedIds: Set<string>
  handleToggle: (rowId: string) => void
  getRowId: (item: T, fallbackId: string) => string
} {
  // Internal state (uncontrolled mode)
  const [internalExpandedIds, setInternalExpandedIds] = useState<Set<string>>(new Set())

  // Determine if controlled
  const isControlled = config?.expandedRowIds !== undefined && config?.onExpandedChange !== undefined
  const expandedIds = isControlled ? config.expandedRowIds! : internalExpandedIds

  // Clear expansion on page change
  const prevPageRef = useRef(pageIndex)
  useEffect(() => {
    if (prevPageRef.current !== pageIndex) {
      if (isControlled) {
        config.onExpandedChange!(new Set())
      } else {
        setInternalExpandedIds(new Set())
      }
      prevPageRef.current = pageIndex
    }
  }, [pageIndex, isControlled])

  const handleToggle = useCallback(
    (rowId: string) => {
      const newSet = new Set(expandedIds)
      if (newSet.has(rowId)) {
        newSet.delete(rowId)
      } else {
        newSet.add(rowId)
      }

      if (isControlled) {
        config.onExpandedChange!(newSet)
      } else {
        setInternalExpandedIds(newSet)
      }
    },
    [expandedIds, isControlled]
  )

  const getRowId = useCallback(
    (item: T, fallbackId: string) => {
      return config?.getRowId ? config.getRowId(item) : fallbackId
    },
    [config?.getRowId]
  )

  return { expandedIds, handleToggle, getRowId }
}

/**
 * Generic PaginatedTable component
 * Provides pagination, filtering, and sorting functionality for any entity type
 */
export function PaginatedTable<T, F extends BaseFilters = BaseFilters>({
  data,
  columns,
  loading,
  error,
  onRetry,
  serverPagination,
  serverFiltering,
  headerActions,
  emptyState = { heading: "No data", text: "No items to display" },
  variant = "hydrologiq",
  entityName = "items",
  exportComponent,
  expandableRows,
  getRowStyle,
  hideControls = false,
  hideHeader = false,
  hidePagination = false,
  isNested = false,
}: PaginatedTableProps<T, F>) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [sorting, setSorting] = useState<SortingState>([])

  // Use server pagination if provided, otherwise use client pagination
  const [clientPagination, setClientPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const isServerPaginated = !!serverPagination
  const isServerFiltered = !!serverFiltering

  const paginationState = isServerPaginated
    ? {
        pageIndex: (serverPagination.pagination.page || 1) - 1,
        pageSize: serverPagination.pagination.per_page || 10,
      }
    : clientPagination

  // Expansion state management
  const { expandedIds, handleToggle, getRowId } = useExpansionState(expandableRows, paginationState.pageIndex)

  // Expand toggle column definition
  const expandColumn: ColumnDef<T, any> = useMemo(
    () => ({
      id: "__expand",
      header: "",
      size: 22,
      minSize: 22,
      meta: { shrink: true },
      cell: ({ row }) => {
        const isRowExpandable = expandableRows?.isExpandable ? expandableRows.isExpandable(row.original) : true
        const rowId = getRowId(row.original, row.id)
        const isExpanded = expandedIds.has(rowId)

        if (!isRowExpandable) {
          return (
            <Box
              as="button"
              type="button"
              disabled
              aria-expanded={false}
              aria-label="Not expandable"
              data-testid="expand-button"
              width="100%"
              maxWidth="1.375rem"
              height="100%"
              minHeight="1.5rem"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              bg="transparent"
              border="none"
              color="#545454"
              opacity={0.3}
              cursor="default"
              p={0}
            >
              <FaChevronRight size={10} style={{ transform: "rotate(0deg)" }} />
            </Box>
          )
        }

        return (
          <Box
            as="button"
            type="button"
            data-testid={isExpanded ? "collapse-button" : "expand-button"}
            onClick={() => handleToggle(rowId)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse row" : "Expand row"}
            width="100%"
            maxWidth="1.375rem"
            height="100%"
            minHeight="1.5rem"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            bg="transparent"
            border="none"
            color="#545454"
            cursor="pointer"
            borderRadius="sm"
            p={0}
            _hover={{ bg: "gray.100", color: "#2B2B2B" }}
            transition="background 0.15s ease"
          >
            <Box
              as="span"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              transition="transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
              transform={isExpanded ? "rotate(90deg)" : "rotate(0deg)"}
            >
              <FaChevronRight size={10} />
            </Box>
          </Box>
        )
      },
      enableSorting: false,
      enableColumnFilter: false,
    }),
    [expandedIds, handleToggle, getRowId]
  )

  // Merge expand column with consumer columns
  const allColumns = useMemo(
    () => (expandableRows ? [expandColumn, ...columns] : columns),
    [expandableRows, expandColumn, columns]
  )

  // Convert server filters to TanStack Table format for display
  const serverColumnFilters = useMemo(() => {
    if (!isServerFiltered) return []

    const filters: ColumnFiltersState = []
    const { filters: serverFilters } = serverFiltering

    // Convert server filters to TanStack Table format
    Object.entries(serverFilters).forEach(([key, value]) => {
      if (value) {
        filters.push({ id: key, value })
      }
    })

    return filters
  }, [isServerFiltered, serverFiltering])

  // Convert server sort to TanStack Table format
  const serverSorting = useMemo(() => {
    if (!isServerFiltered || !serverFiltering.sort) return []

    return [
      {
        id: serverFiltering.sort.field || "",
        desc: serverFiltering.sort.direction === "desc",
      },
    ]
  }, [isServerFiltered, serverFiltering])

  // Custom filter/sort handlers for server-side filtering
  const handleColumnFiltersChange = useCallback(
    (updater: any) => {
      if (isServerFiltered) {
        // Convert TanStack Table filter to server filter
        const newFilters = typeof updater === "function" ? updater(serverColumnFilters) : updater
        const serverFilters: any = {}

        newFilters.forEach((filter: any) => {
          if (filter.value) {
            serverFilters[filter.id] = filter.value
          }
        })

        serverFiltering?.onFiltersChange(serverFilters as F)
      } else {
        setColumnFilters(updater)
      }
    },
    [isServerFiltered, serverColumnFilters, serverFiltering]
  )

  const handleSortingChange = useCallback(
    (updater: any) => {
      if (isServerFiltered) {
        // For server filtering, TanStack Table provides an updater function that expects
        // the current state and returns the new state
        if (typeof updater === "function") {
          const currentSorting = serverSorting
          const newSorting = updater(currentSorting)

          if (newSorting.length > 0) {
            const sort = newSorting[0]
            serverFiltering?.onSortChange({
              field: sort.id,
              direction: sort.desc ? "desc" : "asc",
            })
          } else {
            serverFiltering?.onSortChange(null)
          }
        } else {
          // Direct state update (array of sort objects)
          if (updater.length > 0) {
            const sort = updater[0]
            serverFiltering?.onSortChange({
              field: sort.id,
              direction: sort.desc ? "desc" : "asc",
            })
          } else {
            serverFiltering?.onSortChange(null)
          }
        }
      } else {
        setSorting(updater)
      }
    },
    [isServerFiltered, serverSorting, serverFiltering]
  )

  const table = useReactTable({
    data,
    columns: allColumns,
    filterFns: {
      auto: (row, columnId, value) => {
        const cellValue = row.getValue(columnId)
        if (cellValue === null || cellValue === undefined) {
          return false
        }
        return String(cellValue).toLowerCase().includes(String(value).toLowerCase())
      },
    },
    state: {
      columnFilters: isServerFiltered ? serverColumnFilters : columnFilters,
      sorting: isServerFiltered ? serverSorting : sorting,
      pagination: paginationState,
    },
    onColumnFiltersChange: handleColumnFiltersChange,
    onSortingChange: handleSortingChange,
    onPaginationChange: isServerPaginated ? undefined : setClientPagination,
    manualPagination: isServerPaginated,
    manualFiltering: isServerFiltered,
    manualSorting: isServerFiltered,
    pageCount: isServerPaginated ? serverPagination?.pagination.total_pages : undefined,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  })

  const tbodyRef = useRef<HTMLTableSectionElement>(null)
  const tableBoxRef = useRef<HTMLDivElement>(null)
  const lastWidthRef = useRef<number>(0)

  useEffect(() => {
    const tbody = tbodyRef.current
    if (!tbody) return

    const syncRowHeights = () => {
      const rows = tbody.querySelectorAll<HTMLTableRowElement>('tr[data-table-row="true"]')
      if (rows.length <= 1) return

      // 1. Reset heights to measure natural rendered height
      rows.forEach((row) => {
        row.style.height = ""
        Array.from(row.cells).forEach((cell) => {
          cell.style.height = ""
        })
      })

      // 2. Find maximum height across data rows
      let maxHeight = 0
      rows.forEach((row) => {
        const h = row.getBoundingClientRect().height
        if (h > maxHeight) {
          maxHeight = h
        }
      })

      // 3. Apply uniform height to all data rows
      if (maxHeight > 0) {
        const heightStr = `${Math.ceil(maxHeight)}px`
        rows.forEach((row) => {
          row.style.height = heightStr
          Array.from(row.cells).forEach((cell) => {
            cell.style.height = heightStr
          })
        })
      }
    }

    syncRowHeights()

    const box = tableBoxRef.current
    if (!box || typeof ResizeObserver === "undefined") return

    let rafId: number | null = null
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        const width = entry.contentRect.width
        if (Math.abs(width - lastWidthRef.current) > 1) {
          lastWidthRef.current = width
          if (rafId) cancelAnimationFrame(rafId)
          rafId = requestAnimationFrame(() => {
            syncRowHeights()
          })
        }
      }
    })

    resizeObserver.observe(box)

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      resizeObserver.disconnect()
    }
  }, [data, paginationState, isServerPaginated, allColumns, expandedIds])

  // Show error state
  if (error && !loading) {
    return (
      <VStack gap={4}>
        <ErrorAlert title="Failed to Load Data" message={error} onClose={onRetry ? undefined : () => {}} />
        {onRetry && (
          <Button variant="outline" onClick={onRetry}>
            Try Again
          </Button>
        )}
      </VStack>
    )
  }

  // Show table with data or loading skeletons
  return (
    <VStack
      gap={isNested ? 0 : "0.375rem"}
      align="stretch"
      style={
        isNested
          ? undefined
          : {
              flex: 1,
              paddingBottom: 0,
            }
      }
    >
      {/* Header with actions, clear filters, and item count */}
      {!hideControls && (
        <HStack justify="space-between">
          {/* Left side - Header actions (like Create button) */}
          <Box>{headerActions}</Box>

          {/* Right side - Export, clear filters and item count */}
          <HStack gap={3}>
            {exportComponent}

            <Button
              variant="hydrologiq_secondary"
              size="sm"
              onClick={() => {
                if (isServerFiltered) {
                  serverFiltering?.onClearAll()
                } else {
                  setColumnFilters([])
                  setSorting([])
                }
              }}
              disabled={
                isServerFiltered
                  ? Object.keys(serverFiltering?.filters || {}).length === 0 && !serverFiltering?.sort
                  : columnFilters.length === 0 && sorting.length === 0
              }
            >
              Restore defaults
            </Button>
            <Text fontSize="sm" color="gray.600">
              {isServerPaginated
                ? `${serverPagination.pagination.total_count} total ${entityName}`
                : `${table.getPrePaginationRowModel().rows.length} ${entityName} found`}
            </Text>
          </HStack>
        </HStack>
      )}

      {/* Table */}
      <Box
        ref={tableBoxRef}
        overflowX={isNested ? "visible" : "auto"}
        position="relative"
        width="100%"
      >
        <Table.Root
          variant={variant}
          size="md"
          style={{
            width: "100%",
            ...(isNested
              ? {
                  border: "none",
                  boxShadow: "none",
                  borderRadius: 0,
                  marginTop: 0,
                  background: "transparent",
                  overflow: "visible",
                }
              : {}),
          }}
        >
          {!hideHeader && (
            <Table.Header
              style={{
                position: "sticky",
                top: 0,
                zIndex: 3,
                background: "#DCDCDC",
              }}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Row key={headerGroup.id} style={{ background: "#DCDCDC", minHeight: "3.5rem" }}>
                  {headerGroup.headers.map((header, colIndex) => {
                    const isExpandHeader = header.column.id === "__expand" || header.column.id === "_expand"
                    const isNextToExpand =
                      (expandableRows || allColumns[0]?.id === "__expand" || allColumns[0]?.id === "_expand") &&
                      colIndex === 1

                    return (
                      <Table.ColumnHeader
                        key={header.id}
                        style={{
                          background: "#DCDCDC",
                          minHeight: "3.5rem",
                          verticalAlign: "middle",
                          ...(header.column.columnDef.meta?.shrink
                            ? { width: "max-content", whiteSpace: "nowrap" }
                            : {
                                ...(header.column.columnDef.size ? { width: header.getSize(), maxWidth: header.getSize() } : {}),
                                ...(header.column.columnDef.minSize ? { minWidth: header.column.columnDef.minSize } : {}),
                              }),
                          ...(isExpandHeader
                            ? {
                                paddingLeft: "0.25rem",
                                paddingRight: 0,
                                width: "1.75rem",
                                minWidth: "1.75rem",
                                maxWidth: "1.75rem",
                              }
                            : {
                                padding: "1.25rem 0.625rem",
                              }),
                          ...(isNextToExpand ? { paddingLeft: "0.125rem" } : {}),
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <ColumnHeader
                            header={header}
                            columnFilters={isServerFiltered ? serverColumnFilters : columnFilters}
                          />
                        )}
                      </Table.ColumnHeader>
                    )
                  })}
                </Table.Row>
              ))}
            </Table.Header>
          )}

          <Table.Body ref={tbodyRef}>
            {loading && data.length === 0 ? (
              // Loading skeleton rows (only when no data to show yet)
              [...Array(isNested ? 2 : 10)].map((_, index) => (
                <Table.Row
                  key={`skeleton-${index}`}
                  style={isNested ? { background: "#F8FAFC" } : undefined}
                >
                  {allColumns.map((col, colIndex) => {
                    const isExpand = col.id === "__expand" || col.id === "_expand"
                    const isNextToExpand =
                      (expandableRows || allColumns[0]?.id === "__expand" || allColumns[0]?.id === "_expand") &&
                      colIndex === 1
                    return (
                      <Table.Cell
                        key={colIndex}
                        style={{
                          ...(isExpand
                            ? { width: "1.75rem", minWidth: "1.75rem", maxWidth: "1.75rem", paddingLeft: "0.25rem", paddingRight: 0 }
                            : { padding: "0.55rem 0.625rem" }),
                          ...(isNextToExpand ? { paddingLeft: "0.125rem" } : {}),
                        }}
                      >
                        {isExpand ? null : <Skeleton height="0.875rem" width="100%" />}
                      </Table.Cell>
                    )
                  })}
                </Table.Row>
              ))
            ) : data.length === 0 ? (
              // Empty state row
              <Table.Row style={isNested ? { background: "#F8FAFC" } : undefined}>
                <Table.Cell colSpan={allColumns.length} style={isNested ? { padding: "0.75rem 1rem" } : undefined}>
                  {isNested ? (
                    <Text fontSize="0.8125rem" color="gray.500" fontStyle="italic">
                      {emptyState.heading}
                    </Text>
                  ) : (
                    <Center py={8}>
                      <VStack gap={2}>
                        <Text fontWeight="semibold" color="gray.600">
                          {emptyState.heading}
                        </Text>
                        <Text color="gray.500" fontSize="sm">{emptyState.text}</Text>
                      </VStack>
                    </Center>
                  )}
                </Table.Cell>
              </Table.Row>
            ) : (
              // Actual data rows with expandable content
              table.getRowModel().rows.map((row) => {
                const rowId = expandableRows ? getRowId(row.original, row.id) : ""
                const isExpanded = expandableRows ? expandedIds.has(rowId) : false
                const rowStyle = getRowStyle ? getRowStyle(row.original) : undefined

                return (
                  <React.Fragment key={row.id}>
                    <Table.Row data-table-row="true" style={rowStyle}>
                      {row.getVisibleCells().map((cell, colIndex) => {
                        const isExpandCell = cell.column.id === "__expand" || cell.column.id === "_expand"
                        const isNextToExpand =
                          (expandableRows || allColumns[0]?.id === "__expand" || allColumns[0]?.id === "_expand") &&
                          colIndex === 1

                        const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext())

                        return (
                          <Table.Cell
                            key={cell.id}
                            style={{
                              ...(cell.column.columnDef.meta?.shrink
                                ? { width: "max-content", whiteSpace: "nowrap" }
                                : {
                                    ...(cell.column.columnDef.size ? { width: cell.column.getSize(), maxWidth: cell.column.getSize() } : {}),
                                    ...(cell.column.columnDef.minSize ? { minWidth: cell.column.columnDef.minSize } : {}),
                                  }),
                              ...(isExpandCell
                                ? {
                                    paddingLeft: "0.25rem",
                                    paddingRight: 0,
                                    width: "1.75rem",
                                    minWidth: "1.75rem",
                                    maxWidth: "1.75rem",
                                    verticalAlign: "middle",
                                    height: "100%",
                                  }
                                : {
                                    padding: "0.55rem 0.625rem",
                                    verticalAlign: "middle",
                                  }),
                              ...(isNextToExpand ? { paddingLeft: "0.125rem" } : {}),
                              borderBottom: "1px solid #E2E8F0",
                            }}
                          >
                            {isExpandCell ? (
                              cellContent
                            ) : (
                              <Box
                                minW={0}
                                width="100%"
                                overflow={isNested || isNextToExpand ? "visible" : "hidden"}
                                textOverflow="ellipsis"
                              >
                                {cellContent}
                              </Box>
                            )}
                          </Table.Cell>
                        )
                      })}
                    </Table.Row>

                    {isExpanded && expandableRows && (
                      expandableRows.renderExpandedRows ? (
                        expandableRows.renderExpandedRows(row.original, columns)
                      ) : (
                        <Table.Row style={{ background: "#F8FAFC" }}>
                          <Table.Cell
                            colSpan={allColumns.length}
                            p={expandableRows.cellPadding !== undefined ? expandableRows.cellPadding : undefined}
                            py={expandableRows.cellPadding !== undefined ? undefined : 2}
                            px={expandableRows.cellPadding !== undefined ? undefined : 4}
                            style={{
                              ...(expandableRows.cellPadding === 0 ? { padding: 0 } : {}),
                              borderBottom: "1.5px solid #EEEEEE",
                            }}
                          >
                            {expandableRows.renderExpandedContent?.(row.original)}
                          </Table.Cell>
                        </Table.Row>
                      )
                    )}
                  </React.Fragment>
                )
              })
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Custom Pagination */}
      {!hidePagination && (
        <HStack
          justify="space-between"
          align="center"
          width="100%"
          pt="0.375rem"
          pb="0.375rem"
        >
          <Text fontSize="xs" color="gray.600">
            {isServerPaginated
              ? `Page ${serverPagination.pagination.page} of ${serverPagination.pagination.total_pages}`
              : `${table.getRowModel().rows.length} of ${table.getFilteredRowModel().rows.length} records`}
          </Text>

          <HStack gap={1}>
            {/* Previous button */}
            <Button
              variant="hydrologiq_secondary"
              size="xs"
              onClick={() => {
                if (isServerPaginated) {
                  const currentPage = serverPagination.pagination.page || 1
                  if (currentPage > 1) {
                    serverPagination.onPageChange(currentPage - 1)
                  }
                } else {
                  table.previousPage()
                }
              }}
              disabled={isServerPaginated ? !serverPagination.pagination.has_prev : !table.getCanPreviousPage()}
            >
              &lt;
            </Button>

            {/* Page numbers */}
            {getPageNumbers(
              isServerPaginated ? serverPagination.pagination.total_pages : table.getPageCount(),
              isServerPaginated ? serverPagination.pagination.page : table.getState().pagination.pageIndex + 1
            ).map((pageNumber, index) => (
              <React.Fragment key={index}>
                {pageNumber === "..." ? (
                  <Text px={1.5} fontSize="xs" color="gray.400">
                    ...
                  </Text>
                ) : (
                  <Button
                    size="xs"
                    onClick={() => {
                      if (isServerPaginated) {
                        serverPagination.onPageChange(Number(pageNumber))
                      } else {
                        table.setPageIndex(Number(pageNumber) - 1)
                      }
                    }}
                    variant={
                      pageNumber ===
                      (isServerPaginated ? serverPagination.pagination.page : table.getState().pagination.pageIndex + 1)
                        ? "hydrologiq_primary"
                        : "hydrologiq_secondary"
                    }
                  >
                    {pageNumber}
                  </Button>
                )}
              </React.Fragment>
            ))}

            {/* Next button */}
            <Button
              variant="hydrologiq_secondary"
              size="xs"
              onClick={() => {
                if (isServerPaginated) {
                  serverPagination.onPageChange(serverPagination.pagination.page + 1)
                } else {
                  table.nextPage()
                }
              }}
              disabled={isServerPaginated ? !serverPagination.pagination.has_next : !table.getCanNextPage()}
            >
              &gt;
            </Button>
          </HStack>

          {/* Page size selector */}
          <HStack gap={2}>
            <NativeSelectRoot size="xs" width="auto">
              <NativeSelectField
                style={{ borderRadius: "5px" }}
                value={isServerPaginated ? serverPagination.pagination.per_page : table.getState().pagination.pageSize}
                onChange={(e) => {
                  if (isServerPaginated) {
                    serverPagination.onPageSizeChange(Number(e.target.value))
                  } else {
                    table.setPageSize(Number(e.target.value))
                  }
                }}
              >
                <option value={10}>10 rows</option>
                <option value={25}>25 rows</option>
                <option value={50}>50 rows</option>
              </NativeSelectField>
            </NativeSelectRoot>
          </HStack>
        </HStack>
      )}
    </VStack>
  )
}

export default PaginatedTable
